/**
 * What an operator has chosen about their own console: the palette, and which dashboard widgets they
 * want in which order drawn which way.
 *
 * The preference is per account rather than per tenant. Two people looking after the same fleet do
 * not have to agree about dark mode, and making them agree would be a strange thing for this product
 * to insist on.
 *
 * One exception, and it is marked where it is declared: `RailChoice` is per browser. It is a fact
 * about the screen somebody is looking at rather than about them, so it never reaches the server and
 * is not part of `ConsolePreference`.
 *
 * Everything here is pure except `applyTheme`, so the merge rules that decide what a saved layout
 * means when the catalog has moved on are testable without a browser.
 */

/**
 * The palettes, by their stored spellings.
 *
 * 'light' and 'dark' read as "Modernist" and "Nocturne" on the settings screen, and the asymmetry
 * with 'broadsheet' is deliberate. Both were already written against accounts and are validated by
 * `ConsoleCatalog.Themes` server-side, so renaming them would be a data migration plus a server
 * release to change two strings nobody sees.
 *
 * 'system' is still the default value the server returns for an account with no stored row - but what
 * it *paints* in a light room is now Coastline, because the stylesheet's bare `:root` holds the
 * Coastline values (D-520); it was Broadsheet before that, and Modernist before that (D-307). An
 * account that never opened this screen and an account that chose "Follow the system" are the same
 * row here, so redefining what that row paints is the only way to repaint the first without
 * rewriting the second's choice.
 */
export type ThemeChoice = 'system' | 'light' | 'dark' | 'broadsheet' | 'coastline'

export const THEME_CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark', 'broadsheet', 'coastline']

/**
 * How a widget may be drawn. The server validates against this same list, so a value added here is
 * added there too — see `ConsoleCatalog` in `src/AFilter.Server.Identity/ConfigureConsole.cs`.
 */
export const CHART_KINDS = ['tiles', 'line', 'area', 'bar', 'stacked', 'donut', 'table'] as const

export type ChartKind = (typeof CHART_KINDS)[number]

/**
 * How much of the board's width a widget takes.
 *
 * Three, rather than a column count. A number from one to twelve is more expressive and the
 * expressiveness is not worth having here: it is a second thing to decide for every widget, most of
 * the values differ from their neighbours by an amount nobody can see, and the arrangements it buys
 * over these three are ones somebody has to fiddle with rather than choose. Three is also three
 * buttons rather than a slider, which is what makes putting the control on the widget itself work.
 *
 * The server validates against this same list, so a size added here is added there too - see
 * `ConsoleCatalog` in `src/AFilter.Server.Identity/ConfigureConsole.cs`.
 */
export const WIDGET_SIZES = ['full', 'half', 'third'] as const

export type WidgetSize = (typeof WIDGET_SIZES)[number]

export interface WidgetChoice {
  widget: string
  chart: string
  visible: boolean
  /**
   * Optional on the way in, because every layout stored before sizes existed has no such key and
   * those rows are not rewritten. `resolveLayout` fills it from the catalog, so nothing downstream
   * of that function has to think about it.
   */
  size?: string
}

export interface ConsolePreference {
  theme: ThemeChoice
  dashboardLayout: WidgetChoice[]
}

/** The shape `resolveLayout` needs from the widget catalog, so this file stays free of components. */
export interface WidgetDefinition {
  id: string
  charts: readonly ChartKind[]
  defaultChart: ChartKind
  defaultVisible: boolean
  defaultSize: WidgetSize
  /**
   * The widths this widget may be given, when it cannot take all three.
   *
   * A table of nine columns in a third of the width is not a narrow table, it is an unreadable one,
   * so a widget is allowed to say which sizes make sense for what it draws. Absent means all three.
   */
  sizes?: readonly WidgetSize[]
  /**
   * Kept at the top of the board, above whatever an operator has arranged.
   *
   * `resolveLayout` appends catalog entries the saved layout has never seen, which is right for a
   * new chart and wrong for the ribbon: somebody who arranged their board a month ago would find the
   * thing that says what needs a decision at the very bottom, on the first load after a deploy, and
   * would reasonably conclude it had been put there on purpose.
   *
   * A rule rather than an id special-case, so the rollback property this function's comment defends -
   * an unrecognized widget is dropped, never guessed at - is untouched.
   */
  pinned?: boolean
}

/**
 * Mirrored into storage so the inline script in index.html can stamp the palette before the first
 * paint. The key is spelled out in both places; changing it here means changing it there.
 */
/**
 * Exported so `themeStamp.test.ts` can assert that `index.html`'s pre-paint script reads the same key
 * and accepts the same values. The two are hand-duplicated - the script runs before any module loads,
 * so it cannot import this - and nothing else would notice if they came apart.
 */
export const THEME_KEY = 'afilter.theme'

export function defaultPreference(): ConsolePreference {
  return { theme: 'system', dashboardLayout: [] }
}

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return typeof value === 'string' && (THEME_CHOICES as readonly string[]).includes(value)
}

/**
 * Puts the palette on the document, and remembers it for the next load.
 *
 * "system" removes the attribute rather than setting it to anything, and that is now doing more work
 * than it used to. The stylesheet's `prefers-color-scheme` block is guarded on the attribute being
 * absent or "light" and not "broadsheet"; a `data-theme` of "system" would match neither that guard
 * nor any explicit palette block, so the operator would be pinned to Broadsheet forever rather than
 * following the machine. Removing the attribute is what makes "follow the system" mean Broadsheet in
 * a light room and Nocturne in a dark one.
 */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement

  if (choice === 'system') delete root.dataset.theme
  else root.dataset.theme = choice

  try {
    if (choice === 'system') window.localStorage.removeItem(THEME_KEY)
    else window.localStorage.setItem(THEME_KEY, choice)
  } catch {
    // A private window, or a browser blocking site data. The theme still applies to this page; it
    // simply will not survive a reload, and that is a better outcome than failing to render.
  }
}

/** The palette the pre-paint script used, so the first render agrees with what is already on screen. */
export function cachedTheme(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(THEME_KEY)
    return isThemeChoice(stored) ? stored : 'system'
  } catch {
    return 'system'
  }
}

/**
 * How wide the operator wants the desktop rail: labeled, or icons alone.
 *
 * The one preference in this file that is *not* per account, and deliberately so. `ConsolePreference`
 * round-trips through `ConsoleCatalog` server-side, so putting the rail there would make a server
 * release the price of a convenience about the window somebody happens to be sitting at. A palette
 * should follow a person between machines; how much room they want given to five words should not.
 *
 * 'labeled' is the default because the collapsed rail names its destinations only in a tooltip, and
 * an operator who has not memorised five glyphs has to hover each one to find out where they lead.
 */
export type RailChoice = 'labeled' | 'icons'

export const RAIL_KEY = 'afilter.rail'

/**
 * Read before the first render rather than stamped ahead of the bundle, unlike the theme.
 *
 * There is no flash to prevent here. `index.html` holds an empty root element, so nothing of the
 * shell is painted until the bundle mounts - whereas the body's background *is* visible before React
 * draws, which is the whole reason the palette needs a pre-paint script and this does not.
 *
 * Anything other than the stored 'icons' reads as the default, so a value written by a newer build
 * degrades to the labeled rail rather than to nothing.
 */
export function cachedRail(): RailChoice {
  try {
    return window.localStorage.getItem(RAIL_KEY) === 'icons' ? 'icons' : 'labeled'
  } catch {
    return 'labeled'
  }
}

/** Remembers the choice for the next load. Wrapped for the reason `applyTheme` is: storage throws. */
export function rememberRail(choice: RailChoice): void {
  try {
    window.localStorage.setItem(RAIL_KEY, choice)
  } catch {
    // A private window, or a browser blocking site data. The rail still changes on this page; it
    // simply will not be remembered, which is a better outcome than failing to render.
  }
}

/**
 * Where somebody got to in a walkthrough, so the panel can offer to carry on.
 *
 * Per browser rather than per account, for exactly `RailChoice`'s reason: a palette should follow a
 * person between machines and where they got to in a walkthrough at this desk should not, and putting
 * it in `ConsolePreference` would make a server release and a `ConsoleCatalog` change the price of a
 * convenience.
 *
 * It exists because the URL alone is not enough, which was not obvious until the query was traced
 * through the console's own navigations. A tutorial step rides in `?help=` and `?step=` so a link can
 * carry it - but a `NavLink` to a bare path drops the whole query, and `CasesPage` changes tab with
 * `withTab('', name)` and discards it deliberately. A resume offer that depended on the query would
 * therefore be present some of the time and absent at random from the operator's point of view, which
 * is worse than not offering one.
 *
 * So two homes for one fact, and the precedence rule is the thing that stops them disagreeing:
 * **a URL that carries `help` wins, and this is consulted only when the URL says nothing.** Paste a
 * link at step 2 while this holds step 5 and you get step 2, because the link is what you just asked
 * for.
 *
 * Not a completion log and not a first-run flag. Nothing here decides whether a walkthrough has been
 * read, and nothing opens itself: a modal that greets somebody on first sign-in is the one every
 * operator dismisses without reading.
 */
export const TUTORIAL_KEY = 'afilter.tutorial'

export interface TutorialProgress {
  id: string
  /** One-based, and not validated here. The reader clamps it against the tutorial it names. */
  step: number
  /**
   * The reader closed the card rather than finishing, so the place is kept and offered in the Help
   * menu but the card does not reappear by itself on every screen they visit afterwards.
   *
   * Absent in a value an older build wrote, which reads as false: that build had no card to put
   * down, only a dialog that was always closed until somebody opened it.
   */
  putDown: boolean
}

/**
 * Whatever is stored, or null.
 *
 * Every shape of rubbish resolves to null rather than throwing: a value written by a newer build, a
 * step that is not a number, a tutorial that no longer exists, a private window, a browser blocking
 * site data. Losing a resume offer is a smaller failure than failing to render the panel.
 */
export function cachedTutorial(): TutorialProgress | null {
  try {
    const raw = window.localStorage.getItem(TUTORIAL_KEY)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null

    const { id, step } = parsed as Partial<TutorialProgress>
    if (typeof id !== 'string' || id.length === 0) return null
    if (typeof step !== 'number' || !Number.isFinite(step)) return null

    const putDown = (parsed as { putDown?: unknown }).putDown === true

    return { id, step: Math.trunc(step), putDown }
  } catch {
    return null
  }
}

/** Remembers the step. Wrapped for the reason `applyTheme` is: storage throws. */
export function rememberTutorial(progress: TutorialProgress): void {
  try {
    window.localStorage.setItem(TUTORIAL_KEY, JSON.stringify(progress))
  } catch {
    // A private window, or a browser blocking site data. The walkthrough still works on this page; it
    // simply will not offer to resume after a reload.
  }
}

/** Forgets it, when a walkthrough is closed rather than navigated away from. */
export function forgetTutorial(): void {
  try {
    window.localStorage.removeItem(TUTORIAL_KEY)
  } catch {
    // As above.
  }
}

/**
 * Reconciles a saved layout with the catalog the running build actually has.
 *
 * A saved layout is data written by an older or newer console, so all four kinds of drift are
 * ordinary rather than exceptional: a widget this build no longer has, a widget it has gained, a
 * chart kind that has been renamed, and the same widget saved twice. Dropping, appending, falling
 * back and de-duplicating are handled here so that no caller has to think about it, and so that a
 * layout saved by a newer build survives a rollback instead of being destroyed by it.
 *
 * Order is the saved order first, then anything new in catalog order. A widget added in a later
 * release appears at the bottom, which is where somebody who has already arranged their dashboard
 * would expect to find it.
 */
export function resolveLayout(
  saved: readonly WidgetChoice[],
  catalog: readonly WidgetDefinition[]
): WidgetChoice[] {
  const known = new Map(catalog.map((widget) => [widget.id, widget]))
  const placed = new Set<string>()
  const layout: WidgetChoice[] = []

  for (const choice of saved) {
    const definition = known.get(choice.widget)
    if (!definition || placed.has(choice.widget)) continue

    placed.add(choice.widget)
    layout.push({
      widget: choice.widget,
      chart: (definition.charts as readonly string[]).includes(choice.chart)
        ? choice.chart
        : definition.defaultChart,
      visible: choice.visible,
      // The same fallback the chart kind gets, for the same reason: a size this build does not have,
      // or one the widget does not allow, resolves to the catalog's rather than being stored back.
      // A layout written before sizes existed carries no size at all and lands here too.
      size: (sizesFor(definition) as readonly string[]).includes(choice.size ?? '')
        ? choice.size!
        : definition.defaultSize
    })
  }

  for (const definition of catalog) {
    if (placed.has(definition.id)) continue
    layout.push({
      widget: definition.id,
      chart: definition.defaultChart,
      visible: definition.defaultVisible,
      size: definition.defaultSize
    })
  }

  // A pinned widget stays at the top even when the operator's saved order has never heard of it.
  //
  // Appending is right for a new chart and wrong for the ribbon: somebody who arranged their board a
  // month ago would find the panel that says what needs a decision at the very bottom on the first
  // load after a deploy, and would reasonably read that as deliberate. A stable partition rather than
  // a sort, so everything else keeps exactly the order it had.
  const pinned = new Set(catalog.filter((widget) => widget.pinned).map((widget) => widget.id))
  if (pinned.size === 0) return layout

  return [...layout.filter((choice) => pinned.has(choice.widget)), ...layout.filter((choice) => !pinned.has(choice.widget))]
}

/** The widths a widget offers, which is all three unless it has said otherwise. */
export function sizesFor(definition: WidgetDefinition): readonly WidgetSize[] {
  return definition.sizes ?? WIDGET_SIZES
}

/** Moves one widget up or down by a single place. Returns a new array; out-of-range moves are no-ops. */
export function moveWidget(layout: readonly WidgetChoice[], widget: string, direction: -1 | 1): WidgetChoice[] {
  const from = layout.findIndex((choice) => choice.widget === widget)
  const to = from + direction
  if (from < 0 || to < 0 || to >= layout.length) return [...layout]

  const moved = [...layout]
  const [choice] = moved.splice(from, 1)
  if (choice) moved.splice(to, 0, choice)
  return moved
}

/** Replaces one widget's entry. Returns a new array; an unknown widget is left alone. */
export function updateWidget(
  layout: readonly WidgetChoice[],
  widget: string,
  change: Partial<Omit<WidgetChoice, 'widget'>>
): WidgetChoice[] {
  return layout.map((choice) => (choice.widget === widget ? { ...choice, ...change } : choice))
}
